import { useState } from 'react'

interface FAQItem {
  id: string
  question: string
  answer: string | JSX.Element
  keywords?: string
}

const faqs: FAQItem[] = [
  {
    id: 'good-seer',
    question: 'What is a good SEER rating?',
    keywords: 'seer rating calculator',
    answer: (
      <>
        <p className="mb-3">
          A <strong className="text-slate-200">SEER (Seasonal Energy Efficiency Ratio)</strong> rating measures how
          efficiently your air conditioner uses electricity over an entire cooling season. Higher is always better.
        </p>
        <ul className="space-y-1.5 list-none">
          {[
            { range: 'SEER 13–14', desc: 'Minimum standard (federally required)', color: 'text-yellow-400' },
            { range: 'SEER 15–17', desc: 'Above-average efficiency — a solid upgrade', color: 'text-lime-400' },
            { range: 'SEER 18–21', desc: 'High efficiency — significant annual savings', color: 'text-green-400' },
            { range: 'SEER 22+', desc: 'Premium/inverter-driven — maximum savings', color: 'text-emerald-400' },
          ].map((tier) => (
            <li key={tier.range} className="flex items-start gap-2">
              <span className="mt-0.5 text-slate-500" aria-hidden="true">▶</span>
              <span>
                <strong className={tier.color}>{tier.range}</strong>
                <span className="text-slate-400"> — {tier.desc}</span>
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-slate-400">
          As of January 2023, the U.S. DOE mandates a minimum of SEER 14–15 for new equipment depending on climate
          region. Use our <strong className="text-slate-300">seer rating savings calculator</strong> above to
          quantify exactly how much a higher-SEER system will save you.
        </p>
      </>
    ),
  },
  {
    id: 'seer-calculated',
    question: 'How is SEER efficiency calculated?',
    keywords: 'seer efficiency calculator',
    answer: (
      <>
        <p className="mb-3">
          SEER is calculated by dividing the total cooling output (in BTU) provided over an entire cooling season by
          the total energy consumed (in watt-hours) during the same period:
        </p>
        <div className="rounded-xl bg-slate-950/70 border border-slate-700/50 p-4 mb-3 font-mono text-sm">
          <p className="text-blue-400">
            SEER = Total BTU of cooling output ÷ Total Wh of electrical energy
          </p>
        </div>
        <p className="mb-3 text-slate-400">
          For cost estimation, our <strong className="text-slate-300">energy savings calculator HVAC</strong> uses:
        </p>
        <div className="rounded-xl bg-slate-950/70 border border-slate-700/50 p-4 mb-3 font-mono text-sm space-y-1">
          <p className="text-cyan-400">kW drawn = (Tons × 12,000 BTU/hr) ÷ (SEER × 1,000)</p>
          <p className="text-cyan-400">Annual Cost = kW × Run Hours × $/kWh</p>
        </div>
        <p className="text-slate-400">
          For example, a 3-ton SEER 14 unit draws{' '}
          <strong className="text-slate-300">2.57 kW</strong> vs. a SEER 20 unit drawing{' '}
          <strong className="text-slate-300">1.80 kW</strong> — a 30% reduction in electricity use.
        </p>
      </>
    ),
  },
  {
    id: 'what-is-hspf',
    question: 'What is HSPF and how does it relate to a heat pump seer calculator?',
    keywords: 'heat pump seer calculator',
    answer: (
      <>
        <p className="mb-3">
          <strong className="text-slate-200">HSPF (Heating Seasonal Performance Factor)</strong> is the heating
          efficiency rating for heat pumps — the heating equivalent of SEER. It measures the total heat delivered
          (in BTU) per watt-hour of electricity consumed over a heating season.
        </p>
        <div className="rounded-xl bg-slate-950/70 border border-slate-700/50 p-4 mb-3 font-mono text-sm">
          <p className="text-orange-400">HSPF = Total BTU heating output ÷ Total Wh of electrical energy</p>
        </div>
        <p className="mb-3 text-slate-400">
          The DOE requires a minimum <strong className="text-slate-300">HSPF of 8.2</strong> for split-system heat
          pumps. Modern inverter-driven systems reach <strong className="text-slate-300">HSPF 10–14</strong>.
        </p>
        <p className="text-slate-400">
          Our <strong className="text-slate-300">heat pump SEER calculator</strong> (Tab B above) uses the same
          formula framework applied to HSPF so you can model heating-season savings with the same precision.
        </p>
      </>
    ),
  },
  {
    id: 'inverter',
    question: 'Does an inverter-driven system actually save money?',
    keywords: 'hvac seer calculator inverter',
    answer: (
      <>
        <p className="mb-3">
          Yes — inverter-driven (variable-speed) compressors are the primary reason modern equipment achieves SEER
          20+ and HSPF 10+ ratings. Unlike single-stage units that cycle fully on/off, an inverter compressor
          modulates its speed to match the exact load required.
        </p>
        <ul className="space-y-2 mb-3">
          {[
            'Runs at low capacity (~30–60%) for the majority of hot but not extreme days',
            'Eliminates energy-wasting start-up surges that occur during single-stage on/off cycling',
            'Maintains steadier indoor temperature with fewer humidity swings',
            'Reduces wear and extends equipment lifespan',
          ].map((point) => (
            <li key={point} className="flex items-start gap-2 text-slate-400">
              <span className="text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true">✓</span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
        <p className="text-slate-400">
          Real-world data shows inverter systems use <strong className="text-slate-300">25–50% less electricity</strong>{' '}
          than standard single-stage units in moderate climates. Use our{' '}
          <strong className="text-slate-300">AC SEER calculator</strong> above to enter your target SEER 20+ rating
          and see the exact dollar savings for your home.
        </p>
      </>
    ),
  },
]

export default function FAQ() {
  const [openId, setOpenId] = useState<string | null>('good-seer')

  const toggle = (id: string) =>
    setOpenId((prev) => (prev === id ? null : id))

  return (
    <div id="faq" className="scroll-mt-20" aria-labelledby="faq-heading">
      <div className="text-center mb-10">
        <h2
          id="faq-heading"
          className="text-2xl sm:text-3xl font-bold text-white mb-3"
        >
          Frequently Asked Questions
        </h2>
        <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
          Everything you need to know about SEER ratings, HSPF, and how our{' '}
          <strong className="text-slate-300">HVAC SEER calculator</strong> works.
        </p>
      </div>

      <div
        className="space-y-3"
        role="list"
        aria-label="Frequently asked questions about SEER and HSPF efficiency"
      >
        {faqs.map((faq) => {
          const isOpen = openId === faq.id
          return (
            <div
              key={faq.id}
              role="listitem"
              className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                isOpen
                  ? 'border-blue-500/30 bg-slate-900/80'
                  : 'border-slate-700/40 bg-slate-900/40 hover:border-slate-600/60'
              }`}
            >
              <button
                type="button"
                id={`faq-btn-${faq.id}`}
                aria-expanded={isOpen}
                aria-controls={`faq-panel-${faq.id}`}
                onClick={() => toggle(faq.id)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:ring-inset"
              >
                <h3 className="text-sm sm:text-base font-semibold text-slate-200 leading-snug">
                  {faq.question}
                </h3>
                <span
                  aria-hidden="true"
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center border transition-all duration-200 ${
                    isOpen
                      ? 'bg-blue-600 border-blue-600 text-white rotate-45'
                      : 'border-slate-600 text-slate-400'
                  }`}
                >
                  +
                </span>
              </button>

              <div
                id={`faq-panel-${faq.id}`}
                role="region"
                aria-labelledby={`faq-btn-${faq.id}`}
                hidden={!isOpen}
                className="px-5 pb-5 text-sm leading-relaxed"
              >
                <div className="pt-1 border-t border-slate-700/40 mt-0.5 pt-4">
                  {faq.answer}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Schema.org FAQ structured data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: typeof faq.answer === 'string'
                  ? faq.answer
                  : faq.question,
              },
            })),
          }),
        }}
      />
    </div>
  )
}
